import { Link } from 'react-router-dom';
import { WeekState } from '@pickem/shared';
import { useApi } from '../lib/useApi';
import { Spinner, ErrorBanner, StatTile, SectionTitle, EmptyState, Card, InfoBanner } from '../components/ui';
import { Wordmark } from '../components/Brand';
import { ordinal, formatDateTime, timeUntil, weekStateLabel } from '../lib/format';

interface DashboardData {
  noLeague?: boolean;
  season: { id: string; year: number; name: string } | null;
  currentWeek: {
    id: string;
    weekNumber: number;
    displayName: string;
    state: WeekState;
    submissionsOpen: boolean;
    locked: boolean;
    opensAt: string | null;
    locksAt: string | null;
    hasSubmitted: boolean;
    gamesCount: number;
    pickPath: string | null;
  } | null;
  standing: {
    rank: number;
    totalLeaguePoints: number;
    totalCorrectPicks: number;
    weeksPlayed: number;
  } | null;
  totalPlayers: number;
  history: {
    weekId: string;
    weekNumber: number;
    weekName: string;
    placement: number | null;
    leaguePoints: number;
    correctPicks: number;
    gamesPicked: number;
  }[];
}

export function DashboardPage() {
  const { data, error, loading } = useApi<DashboardData>('/dashboard');

  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} />;
  if (data?.noLeague) {
    return (
      <div className="flex flex-col items-center gap-4 py-16 text-center">
        <Wordmark showTag={false} className="justify-center" />
        <div>
          <h1 className="font-display text-2xl uppercase text-ink">No League Yet</h1>
          <p className="mt-2 max-w-sm text-sm text-muted">
            Scan a Lost Grove Pick&nbsp;’Em QR code at either location to join.
          </p>
        </div>
      </div>
    );
  }
  if (!data || !data.season) {
    return (
      <EmptyState title="Season not open yet">
        The Lost Grove Pick&nbsp;’Em season hasn’t started. Check back soon!
      </EmptyState>
    );
  }

  const cw = data.currentWeek;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-4xl uppercase text-ink">Your Season</h1>
        <p className="text-sm text-muted">{data.season.name}</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatTile
          label="Season rank"
          value={data.standing ? ordinal(data.standing.rank) : '—'}
          sub={data.standing ? `of ${data.totalPlayers} players` : 'No results yet'}
        />
        <StatTile
          label="Points"
          value={data.standing?.totalLeaguePoints ?? 0}
          sub={`${data.standing?.weeksPlayed ?? 0} weeks played`}
        />
        <StatTile
          label="Correct picks"
          value={data.standing?.totalCorrectPicks ?? 0}
          sub="season total"
        />
      </div>

      {/* Current week — links straight to the pick form when open (no QR scan required). */}
      <section>
        <SectionTitle>This Week</SectionTitle>
        {cw ? (
          <Card>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="font-display text-3xl uppercase text-grove">{cw.displayName}</div>
                <span className="chip mt-1 bg-grove/10 text-grove">{weekStateLabel(cw.state)}</span>
              </div>
              <div className="text-right text-sm">
                {cw.locksAt && (
                  <div className="text-muted">
                    Picks lock <span className="font-medium text-body">{formatDateTime(cw.locksAt)}</span>
                    {cw.submissionsOpen && (
                      <span className="ml-1 text-ember">({timeUntil(cw.locksAt)})</span>
                    )}
                  </div>
                )}
                <div className="text-muted">{cw.gamesCount} Sunday games</div>
              </div>
            </div>

            <div className="mt-4">
              {/* Locked is checked first: once picks lock, everyone (including
                  players who submitted) gets the live results link, not the
                  stale "update until lock" message. */}
              {cw.locked ? (
                <div className="rounded border border-line bg-cream px-4 py-3 text-sm text-muted">
                  Picks for {cw.displayName} are closed.{' '}
                  <Link to={`/results/${cw.id}`} className="font-medium text-grove underline">
                    See the live results →
                  </Link>
                </div>
              ) : cw.hasSubmitted ? (
                <div className="rounded border border-success/30 bg-success/5 px-4 py-3 text-sm">
                  ✓ Your picks are in for {cw.displayName}. You can update them any time until picks
                  lock.
                  {cw.pickPath && (
                    <Link to={cw.pickPath} className="btn-outline mt-3 w-full">
                      Update your picks →
                    </Link>
                  )}
                </div>
              ) : cw.submissionsOpen ? (
                cw.pickPath ? (
                  <Link to={cw.pickPath} className="btn-primary btn-lg w-full">
                    Make your picks →
                  </Link>
                ) : (
                  <InfoBanner>
                    Picks are <strong>open</strong> for {cw.displayName}. The pick form isn’t ready
                    just yet — check back in a few minutes.
                  </InfoBanner>
                )
              ) : (
                <div className="rounded border border-line bg-cream px-4 py-3 text-sm text-muted">
                  Picks aren’t open yet. Come into Lost Grove and scan the QR code when they are.
                </div>
              )}
            </div>
          </Card>
        ) : (
          <EmptyState title="No week scheduled">
            There’s no Pick&nbsp;’Em week set up right now. Check back soon.
          </EmptyState>
        )}
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <SectionTitle>Recent Weeks</SectionTitle>
          <Link to="/standings" className="text-sm text-grove underline">
            Full standings →
          </Link>
        </div>
        {data.history.length === 0 ? (
          <EmptyState title="No history yet">
            Once a week wraps up, your placement and points will show here.
          </EmptyState>
        ) : (
          <div className="space-y-2">
            {data.history.map((h) => (
              <Link
                key={h.weekId}
                to={`/results/${h.weekId}`}
                className="card flex items-center justify-between px-4 py-3 transition-colors hover:bg-cream"
              >
                <div>
                  <div className="font-medium">{h.weekName}</div>
                  <div className="text-sm text-muted">
                    {h.correctPicks}/{h.gamesPicked} correct
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-2xl text-grove">{ordinal(h.placement)}</div>
                  <div className="text-sm text-muted">+{h.leaguePoints} pts</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
